import { alert } from '@/services/alert';
import { useMedplum } from '@medplum/react';
import { Practitioner } from '@medplum/fhirtypes';
import { ReactElement, useEffect, useState } from 'react';
import { Button, Drawer, Group, Stack, TextInput } from '@mantine/core';
import { getOperationOutcomeMessage, isVersionConflictOutcome } from '@bariendo/lib/error';
import { getPractitionerWorkPhoneContactIndex } from '@bariendo/lib/profile';
import {
  cleanPhoneNumberForEditing,
  formatContactPointValue,
  isAValidPhoneNumber,
  normalizePhoneNumber,
} from '@bariendo/lib/phone-validations';

interface EditPhoneNumberDrawerProps {
  opened: boolean;
  onClose: () => void;
  providerName: string;
  practitioner: Practitioner | null;
  onSaved: (updatedPractitioner: Practitioner) => void;
}

function getEditablePhoneNumber(practitioner: Practitioner | null): string {
  if (!practitioner) {
    return '';
  }

  const phoneIndex = getPractitionerWorkPhoneContactIndex(practitioner);
  if (phoneIndex < 0) {
    return '';
  }

  const phoneContact = practitioner.telecom?.[phoneIndex];
  if (!phoneContact?.value?.trim()) {
    return '';
  }

  return cleanPhoneNumberForEditing(formatContactPointValue(phoneContact));
}

function buildPhoneTelecomPatchOperations(
  practitioner: Practitioner,
  phoneNumber: string
): Array<{ op: 'test' | 'replace' | 'add'; path: string; value?: unknown }> {
  const existingIndex = getPractitionerWorkPhoneContactIndex(practitioner);
  const phoneContact = { system: 'phone' as const, use: 'work' as const, value: phoneNumber };

  const telecomOp =
    existingIndex >= 0
      ? { op: 'replace' as const, path: `/telecom/${existingIndex}`, value: phoneContact }
      : practitioner.telecom !== undefined
        ? { op: 'add' as const, path: '/telecom/-', value: phoneContact }
        : { op: 'add' as const, path: '/telecom', value: [phoneContact] };

  return [{ op: 'test', path: '/meta/versionId', value: practitioner.meta?.versionId }, telecomOp];
}

export function EditPhoneNumberDrawer({
  opened,
  onClose,
  providerName,
  practitioner,
  onSaved,
}: EditPhoneNumberDrawerProps): ReactElement {
  const medplum = useMedplum();
  const [currentPractitioner, setCurrentPractitioner] = useState<Practitioner | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!opened) {
      return;
    }

    setCurrentPractitioner(practitioner);
    setPhoneNumber(getEditablePhoneNumber(practitioner));
  }, [opened, practitioner]);

  const handleSave = async () => {
    if (!currentPractitioner) {
      return;
    }

    if (!currentPractitioner.id) {
      alert.error('Cannot update phone number: missing provider');
      return;
    }

    const practitionerId = currentPractitioner.id;

    const trimmedPhoneNumber = phoneNumber.trim();
    if (!trimmedPhoneNumber) {
      alert.error('Phone number is required');
      return;
    }

    if (!isAValidPhoneNumber(trimmedPhoneNumber)) {
      alert.error('Enter a valid phone number');
      return;
    }

    setSaving(true);
    try {
      const normalizedPhoneNumber = normalizePhoneNumber(trimmedPhoneNumber);
      const patch = buildPhoneTelecomPatchOperations(currentPractitioner, normalizedPhoneNumber);
      const savedPractitioner = await medplum.patchResource('Practitioner', practitionerId, patch);
      onSaved(savedPractitioner);
      onClose();
    } catch (error) {
      if (isVersionConflictOutcome(error)) {
        try {
          const refreshedPractitioner = await medplum.readResource('Practitioner', practitionerId);
          setCurrentPractitioner(refreshedPractitioner);
          onSaved(refreshedPractitioner);
          alert.error('Failed to update phone number: the provider was updated in the meantime. Please try again.');
        } catch {
          alert.error('Failed to refresh provider after conflict. Please reload the page and try again.');
        }
        return;
      }

      alert.error('Failed to update phone number: ' + getOperationOutcomeMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer.Root opened={opened} onClose={onClose} position="right" size="sm">
      <Drawer.Overlay />
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title fw="bold">Edit phone number</Drawer.Title>
          <Drawer.CloseButton />
        </Drawer.Header>

        <Drawer.Body>
          <Stack gap="md" py="sm">
            <TextInput label="Provider name" value={providerName} disabled />

            <TextInput
              label="Phone number"
              placeholder="Enter phone number"
              value={phoneNumber}
              onChange={(event) => setPhoneNumber(event.currentTarget.value)}
              required
            />

            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={onClose} radius="xl" disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void handleSave()} radius="xl" color="blue" loading={saving}>
                Save
              </Button>
            </Group>
          </Stack>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
}
